import { Button } from "@xefi/x-react/button";
import { Tooltip } from "@xefi/x-react/tooltip";
import type { FormikProps } from "formik";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useTranslation } from "react-i18next";
import { SurveyTakingAuth } from "@/app/(survey-taking)/_components/SurveyTakingPage/SurveyTakingAuth";
import { SurveyTakingQuestion } from "@/app/(survey-taking)/_components/SurveyTakingQuestion/SurveyTakingQuestion";
import type { Question } from "@/models/question";
import type { SurveyTaking } from "@/models/survey";
import type { SurveyPage as SurveyPageType } from "@/models/surveyPage";
import { useMainStore } from "@/store";
import { getObjectKey } from "@/utils/common";
import type { SurveyTakingFormValues } from "../../[surveyId]/_components/SurveyTaking";
type SurveyTakingPageProps = {
  page: SurveyPageType;
  index: number;
  hasAuthentication: boolean;
  isLastPage: boolean;
  onPageChange: (next: boolean) => void;
  errorMap: Map<string, string>;
  currentSurvey?: SurveyTaking;
  isSubmitting: boolean;
} & Omit<FormikProps<SurveyTakingFormValues>, "isSubmitting">;
export const isQuestionOverflowed = (question: Question) => {
  return question.is_limit_overflowed || ["radio", "checkbox", "select"].includes(question.question_type?.slug ?? "") && question.linked_choices?.every(choice => choice.is_limit_overflowed);
};
export const SurveyTakingPage = ({
  page,
  index,
  hasAuthentication,
  isLastPage,
  onPageChange,
  setFieldValue,
  values,
  errorMap,
  currentSurvey,
  isSubmitting
}: SurveyTakingPageProps) => {
  const {
    t
  } = useTranslation();
  const {
    currentUser,
    canUserAnswerSurvey
  } = useMainStore();
  let questionNumber = 0;
  const pathname = usePathname();
  const isPreview = pathname?.includes("preview");
  const isFirstPage = index === 0;
  const footerText = currentSurvey?.footer_text ?? currentUser?.site?.client.default_footer_text;
  const url = currentSurvey?.media.find(item_0 => item_0.collection_name === "footer") ?? currentUser?.site?.client.media.find(item => item.collection_name === "default_footer");
  const questions = (currentSurvey?.is_shufflable ? page?.shuffled_questions : page?.questions) ?? [];
  const clearQuestionChoices = (path: string) => {
    const current = getObjectKey(values, path);
    if (current?.linked_questions) {
      for (const [linkedQuestionsIndex, linkedQuestions] of current.linked_questions.entries()) {
        if (linkedQuestions?.answer) {
          setFieldValue(`${path}.linked_questions[${linkedQuestionsIndex}].answer`, undefined, false);
        }
        if (linkedQuestions?.is_selected) {
          setFieldValue(`${path}.linked_questions[${linkedQuestionsIndex}].is_selected`, false, false);
        }
        if (linkedQuestions?.select_value) {
          setFieldValue(`${path}.linked_questions[${linkedQuestionsIndex}].select_value`, undefined, false);
        }
        if (linkedQuestions.linked_choices) {
          for (const [questionChoicesIndex, questionChoices] of linkedQuestions.linked_choices.entries()) {
            if (questionChoices?.is_selected) {
              setFieldValue(`${path}.is_selected`, false, false);
            }
            if (questionChoices?.answer) {
              setFieldValue(`${path}.answer`, undefined, false);
            }
            if (questionChoices?.select_value) {
              setFieldValue(`${path}.select_value`, undefined, false);
            }
            clearQuestionChoices(`${path}.linked_questions[${linkedQuestionsIndex}].linked_choices[${questionChoicesIndex}]`);
          }
        }
      }
    }
    if (current?.linked_choices) {
      for (const [questionChoicesIndex_0, questionChoices_0] of current.linked_choices.entries()) {
        if (questionChoices_0?.is_selected) {
          setFieldValue(`${path}.linked_choices[${questionChoicesIndex_0}].is_selected`, false, false);
        }
        if (questionChoices_0?.select_value) {
          setFieldValue(`${path}.linked_choices[${questionChoicesIndex_0}].select_value`, undefined, false);
        }
        if (questionChoices_0?.answer) {
          setFieldValue(`${path}.linked_choices[${questionChoicesIndex_0}].answer`, undefined, false);
        }
        if (questionChoices_0.linked_questions) {
          for (const [linkedQuestionsIndex_0, linkedQuestions_0] of questionChoices_0.linked_questions.entries()) {
            if (linkedQuestions_0?.is_selected) {
              setFieldValue(`${path}.linked_choices[${questionChoicesIndex_0}].linked_questions[${linkedQuestionsIndex_0}].is_selected`, false, false);
            }
            if (linkedQuestions_0?.select_value) {
              setFieldValue(`${path}.linked_choices[${questionChoicesIndex_0}].linked_questions[${linkedQuestionsIndex_0}].select_value`, undefined, false);
            }
            if (linkedQuestions_0?.answer) {
              setFieldValue(`${path}.linked_choices[${questionChoicesIndex_0}].linked_questions[${linkedQuestionsIndex_0}].answer`, undefined, false);
            }
            clearQuestionChoices(`${path}.linked_choices[${questionChoicesIndex_0}].linked_questions[${linkedQuestionsIndex_0}]`);
          }
        }
      }
    }
    return;
  };
  return <div className="flex max-h-full min-h-full w-full flex-col gap-6 overflow-y-auto px-10 py-4">
         <div className="flex flex-1 flex-col gap-16">
            <div className="flex flex-1 flex-col gap-12">
               {page?.label && <h3 className="text-lg font-bold">{page.label}</h3>}
               {hasAuthentication && index === 0 && <SurveyTakingAuth values={values.authentication_form} handleChange={(field, value) => setFieldValue(`authentication_form.${field}`, value, false)} error={errorMap.get("auth")} currentSurvey={currentSurvey} />}
               {questions.map((question, i) => {
          const pageQuestions = values.pages?.[index]?.questions ?? [];
          const actualIndex = pageQuestions.findIndex(pageQuestion => pageQuestion?.id === question.id);
          const targetIndex = actualIndex === -1 ? i : actualIndex;
          const formikQuestion = pageQuestions[targetIndex];
          const questionWithValues = formikQuestion ? {
            ...question,
            ...formikQuestion
          } : question;
          return question.is_visible ? <Tooltip key={question.id} content={question.is_limit_overflowed && <p className="text-right">
                                 {t("question.unavailable")}
                              </p>} placement="left" showArrow trigger={<SurveyTakingQuestion question={questionWithValues} currentSurvey={currentSurvey} indexLabel={(++questionNumber).toString()} questionPath={`pages[${index}].questions[${targetIndex}]`} handleChange={(path_0, value_0, oldValue) => {
            const pageQuestionsLocal = values.pages?.[index]?.questions ?? [];
            const actualIndexLocal = pageQuestionsLocal.findIndex(pageQuestion_0 => pageQuestion_0?.id === question.id);
            const targetIndexLocal = actualIndexLocal === -1 ? i : actualIndexLocal;
            setFieldValue(`pages[${index}].questions[${targetIndexLocal}].${path_0}`, value_0, false);
            const isCheckboxSelectValue = question.question_type?.slug === "checkbox" && path_0 === "select_value";
            if ((!value_0 || oldValue) && !isCheckboxSelectValue) {
              const basePath = `pages[${index}].questions[${targetIndexLocal}].${path_0}`.split(".").slice(0, -1).join(".");
              clearQuestionChoices(basePath);
            }
          }} errorMap={errorMap} sessions={currentSurvey?.sessions ?? []} disabled={!!question.is_limit_overflowed} />} /> : null;
        })}
            </div>
            <div className="flex h-full justify-between gap-6 max-[380px]:flex-col">
               {!isFirstPage && <Button variant="bordered" onClick={() => onPageChange(false)}>
                     {t("survey.navigation.previous")}
                  </Button>}
               <Button onClick={() => onPageChange(true)} isDisabled={!isPreview ? !canUserAnswerSurvey : false} className={isFirstPage ? "ml-auto" : ""} isLoading={isSubmitting}>
                  {isLastPage ? t("finish", {
            ns: "common"
          }) : t("survey.navigation.next")}
               </Button>
            </div>
         </div>
         <div className="flex flex-col gap-6">
            <div className="h-0.5 w-full rounded-lg"></div>
            <div className="flex flex-col items-center gap-6">
               {url?.original_url && <Image src={url.original_url} alt="footer image" width={500} height={100} className="h-20 rounded-md object-contain" unoptimized={true} />}
               {footerText && <span className="text-content1-600 flex items-center text-[14px] leading-4.25">
                     {footerText}
                  </span>}
            </div>
         </div>
      </div>;
};